import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Eye,
  DollarSign,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Plus,
  RefreshCw,
  Download,
  FileDown,
  Banknote,
  ArrowLeftRight,
  Users,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  FileText,
  Calendar,
  TrendingDown,
  GraduationCap,
  Receipt,
  FilePlus,
  Trash2,
  Upload,
  Image,
  ExternalLink,
  MoreVertical,
  UserX,
  Ban,
  UserCheck,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';

import AssignAncillaryFeeModal from '../../components/payments/AssignAncillaryFeeModal';
import AssignDiscountModal from '../../components/payments/AssignDiscountModal';
import AssignPaymentScheduleModal from '../../components/payments/AssignPaymentScheduleModal';
import { usePendingDiscounts, useApproveDiscount, useRejectDiscount } from '../../hooks/useDiscounts';
import type { PendingDiscount } from '@/services/tuitionService';
import { apiFetch } from '@/utils/api';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;
// Origin (no /api suffix) for serving stored files under /uploads. Do NOT derive
// this via API_BASE.replace('/api',''): when the API host itself contains "api"
// (prod is api.nexedu.ma) that corrupts the URL and the request falls back to
// the frontend SPA (broken image / blank .html download).
const API_ORIGIN = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

interface FeeItem {
  id: string;
  label: string;
  type: string;
  amount: number;
  description?: string;
  ancillaryFeeAssignmentId?: string;
}

interface Discount {
  id: string;
  discountAmount: number;
  discountConfig?: {
    id: string;
    name: string;
  };
}

interface PaymentInstallment {
  id: string;
  label: string;
  dueDate: string;
  amountDue: number;
  amountPaid: number;
  status: string;
}

interface Payment {
  id: string;
  amount: number;
  paymentDate: string;
  method: string;
  reference?: string;
  receiptNumber?: string;
  status: string;
  cancellationReason?: string;
  scannedCopyUrl?: string;
}

interface StudentAccount {
  id: string;
  studentId: string;
  academicYearId: string;
  totalDue: number;
  totalPaid: number;
  totalDiscount: number;
  balance: number;
  isBlocked: boolean;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    studentCode: string;
    campusId?: string;
    programId?: string;
    filiereInscrit?: string;
    niveauFormation?: number;
  };
  academicYear?: {
    id: string;
    name: string;
  };
  feeItems?: FeeItem[];
  discounts?: Discount[];
  schedule?: {
    installments: PaymentInstallment[];
    template?: {
      name: string;
    };
  };
  payments?: Payment[];
}

interface PendingTransfer {
  id: string;
  studentId: string;
  campusId?: string;
  amount: number;
  transferDate: string;
  receiptUrl: string;
  reference?: string;
  bankName?: string;
  notes?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  submittedAt: string;
  submittedBy: string;
  submittedByRole: string;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    studentCode: string;
  };
  campus?: {
    id: string;
    name: string;
  };
  account?: {
    id: string;
    totalDue: number;
    totalPaid: number;
    balance: number;
  };
}

function getAuthHeaders(): Record<string, string> {
  const tokens = useAuthStore.getState().tokens;
  const user = useAuthStore.getState().user;
  const tenantId = user?.tenantId;

  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || '',
    ...(tokens?.accessToken ? { 'Authorization': `Bearer ${tokens.accessToken}` } : {}),
  };
}

type TabType = 'accounts' | 'transfers' | 'discounts';

export const PaymentManagementPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Read initial tab and filter from URL params
  const initialTab = (searchParams.get('tab') as TabType) || 'accounts';
  const initialFilter = searchParams.get('filter') || 'all';

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [accounts, setAccounts] = useState<StudentAccount[]>([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  // Pre-fill search from the URL (?search=CODE) so deep-links from the student
  // profile land on that student's account.
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(initialFilter);
  const [yearFilter, setYearFilter] = useState<string>('all');

  // Transfer review modal state
  const [selectedTransfer, setSelectedTransfer] = useState<PendingTransfer | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // View account details modal state
  const [selectedAccount, setSelectedAccount] = useState<StudentAccount | null>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [loadingAccountDetails, setLoadingAccountDetails] = useState(false);
  const [accountDetailsExpanded, setAccountDetailsExpanded] = useState<string[]>(['fees', 'schedule']);

  // Record payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAccountForPayment, setSelectedAccountForPayment] = useState<StudentAccount | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'CHECK' | 'BANK_TRANSFER'>('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [scannedCopy, setScannedCopy] = useState<File | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Delete payment modal state
  const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<any>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingPayment, setDeletingPayment] = useState(false);

  // Assign ancillary fee modal state
  const [showAssignFeeModal, setShowAssignFeeModal] = useState(false);
  const [selectedAccountForFee, setSelectedAccountForFee] = useState<StudentAccount | null>(null);

  // Assign discount modal state
  const [showAssignDiscountModal, setShowAssignDiscountModal] = useState(false);
  const [selectedAccountForDiscount, setSelectedAccountForDiscount] = useState<StudentAccount | null>(null);

  // Assign payment schedule modal state
  const [showAssignScheduleModal, setShowAssignScheduleModal] = useState(false);
  const [selectedAccountForSchedule, setSelectedAccountForSchedule] = useState<StudentAccount | null>(null);

  // Delete fee item state
  const [deletingFeeItemId, setDeletingFeeItemId] = useState<string | null>(null);
  const [showDeleteFeeConfirm, setShowDeleteFeeConfirm] = useState(false);
  const [feeItemToDelete, setFeeItemToDelete] = useState<FeeItem | null>(null);

  // Delete discount state
  const [showDeleteDiscountConfirm, setShowDeleteDiscountConfirm] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<{ id: string; discountConfig?: { name?: string } } | null>(null);
  const [deletingDiscount, setDeletingDiscount] = useState(false);

  // Success notification state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [lastRecordedPaymentId, setLastRecordedPaymentId] = useState<string | null>(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  // Suspension modal state
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [accountToSuspend, setAccountToSuspend] = useState<StudentAccount | null>(null);
  const [suspendingAccount, setSuspendingAccount] = useState(false);

  // View scanned copy modal state
  const [showScannedCopyModal, setShowScannedCopyModal] = useState(false);
  const [scannedCopyUrl, setScannedCopyUrl] = useState<string | null>(null);
  const [scannedCopyError, setScannedCopyError] = useState(false);

  // Pending discounts approval
  const { data: pendingDiscountsData, isLoading: loadingPendingDiscounts, refetch: refetchPendingDiscounts } = usePendingDiscounts();
  const approveMutation = useApproveDiscount();
  const rejectMutation = useRejectDiscount();
  const [rejectingDiscountId, setRejectingDiscountId] = useState<string | null>(null);
  const [rejectReasonInput, setRejectReasonInput] = useState('');

  const pendingDiscounts = pendingDiscountsData?.data || [];
  const pendingDiscountsCount = pendingDiscountsData?.meta?.total || 0;

  // Always pull the latest pending discounts when the tab is opened (e.g. after
  // arriving from the dashboard "Remises en attente" card), so a discount just
  // assigned by someone else shows up instead of a stale cached list.
  useEffect(() => {
    if (activeTab === 'discounts') refetchPendingDiscounts();
  }, [activeTab, refetchPendingDiscounts]);

  // Permissions
  const { hasPermission } = usePermissions();
  const canRecordPayment = hasPermission(Permission.FINANCE_PAYMENT_RECORD);
  const canApproveDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_APPROVE);
  const canManageDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_MANAGE);
  const canDeleteDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_DELETE);
  const canViewTransfers = hasPermission(Permission.FINANCE_TRANSFER_VIEW);
  const canApproveTransfers = hasPermission(Permission.FINANCE_TRANSFER_APPROVE);
  const canManageAncillary = hasPermission(Permission.FINANCE_ANCILLARY_MANAGE);
  const canManageSchedules = hasPermission(Permission.FINANCE_SCHEDULE_MANAGE);
  const canCancelPayment = hasPermission(Permission.FINANCE_PAYMENT_CANCEL);
  const canGenerateReceipt = hasPermission(Permission.FINANCE_RECEIPT_GENERATE);
  const canViewSuspensions = hasPermission(Permission.FINANCE_SUSPENSION_VIEW);
  const canManageSuspensions = hasPermission(Permission.FINANCE_SUSPENSION_MANAGE);

  // Actions dropdown state
  const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; showAbove: boolean }>({ top: 0, left: 0, showAbove: false });
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Handle dropdown open/close with position calculation
  const handleOpenDropdown = useCallback((accountId: string, buttonElement: HTMLButtonElement) => {
    if (openActionsDropdown === accountId) {
      setOpenActionsDropdown(null);
      return;
    }

    const rect = buttonElement.getBoundingClientRect();
    const dropdownHeight = 250;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownPosition({
      top: showAbove ? rect.top : rect.bottom + 4,
      left: rect.right - 256,
      showAbove,
    });
    setOpenActionsDropdown(accountId);
  }, [openActionsDropdown]);

  // Close dropdown on click outside or scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-dropdown-trigger]')) {
          setOpenActionsDropdown(null);
        }
      }
    };

    const handleScroll = () => {
      setOpenActionsDropdown(null);
    };

    if (openActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openActionsDropdown]);

  useEffect(() => {
    loadData();
  }, []);

  const [unpriced, setUnpriced] = useState<Array<{ studentCode: string; name: string; niveau: number | null; filiere: string | null; reason: string }>>([]);
  const [showUnpriced, setShowUnpriced] = useState(true);

  const loadDiagnostics = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/financial/tuition/account-diagnostics`);
      if (res.ok) {
        const data = await res.json();
        setUnpriced(Array.isArray(data) ? data : []);
        setShowUnpriced(true);
      }
    } catch {
      /* diagnostics are best-effort */
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setAccountsError(null);
      const [accountsRes, transfersRes] = await Promise.all([
        apiFetch(`${API_BASE}/financial/payments/accounts?limit=500`, { headers: getAuthHeaders() }),
        apiFetch(`${API_BASE}/financial/bank-transfers/pending`, { headers: getAuthHeaders() }),
      ]);

      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setAccounts(data.data || []);
        setAccountsTotal(data.meta?.total ?? (data.data || []).length);
      } else {
        const errorData = await accountsRes.json().catch(() => ({}));
        const errorMsg = errorData.message || `Erreur ${accountsRes.status}: Impossible de charger les comptes étudiants`;
        setAccountsError(errorMsg);
        console.error('Error loading accounts:', errorMsg);
      }

      if (transfersRes.ok) {
        const data = await transfersRes.json();
        setPendingTransfers(data.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setAccountsError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transferId: string, notes?: string) => {
    try {
      setSubmitting(true);
      const response = await apiFetch(`${API_BASE}/financial/bank-transfers/${transferId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes }),
      });

      if (response.ok) {
        const approvalData = await response.json();
        // The backend returns the payment object directly with id
        const paymentId = approvalData?.id || approvalData?.paymentId || approvalData?.data?.id;

        await loadData();
        setShowReviewModal(false);
        setSelectedTransfer(null);
        setLastRecordedPaymentId(paymentId || null);
        setSuccessMessage('Virement approuvé avec succès');
        setShowSuccessModal(true);
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors de l\'approbation');
      }
    } catch (error) {
      console.error('Error approving transfer:', error);
      alert('Erreur lors de l\'approbation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (transferId: string, reason: string) => {
    if (!reason.trim()) {
      alert('Veuillez fournir une raison pour le rejet');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiFetch(`${API_BASE}/financial/bank-transfers/${transferId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });

      if (response.ok) {
        await loadData();
        setShowReviewModal(false);
        setSelectedTransfer(null);
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors du rejet');
      }
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      alert('Erreur lors du rejet');
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewModal = (transfer: PendingTransfer, action: 'approve' | 'reject') => {
    setSelectedTransfer(transfer);
    setReviewAction(action);
    setReviewNotes('');
    setRejectionReason('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = () => {
    if (!selectedTransfer) return;
    if (reviewAction === 'approve') {
      handleApprove(selectedTransfer.id, reviewNotes);
    } else if (reviewAction === 'reject') {
      handleReject(selectedTransfer.id, rejectionReason);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getAccountStatus = (account: StudentAccount) => {
    // Show suspended status only if user has VIEW permission
    if (account.isBlocked && canViewSuspensions) return 'suspended';
    if (account.balance <= 0) return 'paid';
    if (account.totalPaid > 0) return 'partial';
    return 'unpaid';
  };

  const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ size?: number; className?: string }> }> = {
    paid: { label: 'Soldé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    partial: { label: 'Partiel', color: 'bg-amber-100 text-amber-700', icon: Clock },
    unpaid: { label: 'Non payé', color: 'bg-gray-100 text-gray-700', icon: DollarSign },
    overdue: { label: 'En retard', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
    suspended: { label: 'Suspendu', color: 'bg-red-100 text-red-800 ring-1 ring-red-600', icon: UserX },
  };

  const formatPaymentMethod = (method: string) => {
    const methods: Record<string, string> = {
      CASH: 'Espèces',
      CHECK: 'Chèque',
      BANK_TRANSFER: 'Virement bancaire',
    };
    return methods[method] || method;
  };

  const getInstallmentStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'PARTIAL': return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInstallmentStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID': return 'Payé';
      case 'PARTIAL': return 'Partiel';
      case 'OVERDUE': return 'En retard';
      case 'PENDING': return 'En attente';
      default: return status;
    }
  };

  // Calculate cumulative amount due up to today (sum of all past due installments minus paid)
  // This includes all installments whose due date has passed
  const getCumulativeAmountDue = (account: StudentAccount): number | null => {
    if (!account.schedule?.installments || account.schedule.installments.length === 0) {
      return null; // No schedule assigned
    }
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    let totalDue = 0;
    let totalPaid = 0;

    for (const inst of account.schedule.installments) {
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      // Include installments that are due today or in the past (due date <= today)
      if (dueDate <= today) {
        totalDue += inst.amountDue;
        totalPaid += inst.amountPaid;
      }
    }

    return Math.max(0, totalDue - totalPaid);
  };

  // Get the next upcoming payment due (first unpaid installment with future due date)
  // Returns: { amount, date, label } for next future payment, or { isOverdue: true } if all unpaid are overdue, or null if all paid
  const getNextPaymentDue = (account: StudentAccount): { amount: number; date: string; label: string; isOverdue?: boolean } | { isOverdue: true } | null => {
    if (!account.schedule?.installments || account.schedule.installments.length === 0) {
      return null; // No schedule assigned
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    // Find unpaid installments
    const unpaidInstallments = account.schedule.installments.filter(
      inst => inst.status !== 'PAID' && inst.amountPaid < inst.amountDue
    );

    // If no unpaid installments, all is paid
    if (unpaidInstallments.length === 0) {
      return null; // All installments fully paid
    }

    // Find the first unpaid installment with future due date
    const nextFutureInstallment = unpaidInstallments.find(inst => {
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate > today;
    });

    if (nextFutureInstallment) {
      return {
        amount: nextFutureInstallment.amountDue - nextFutureInstallment.amountPaid,
        date: nextFutureInstallment.dueDate,
        label: nextFutureInstallment.label,
      };
    }

    // All unpaid installments are overdue (no future ones)
    return { isOverdue: true };
  };

  // View account details
  const handleViewAccount = async (account: StudentAccount) => {
    setSelectedAccount(account);
    setShowAccountModal(true);
    setLoadingAccountDetails(true);

    try {
      // Fetch detailed account info
      const response = await apiFetch(`${API_BASE}/financial/payments/accounts/${account.studentId}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // The detail endpoint returns the account(s) for this student with the
        // full fee breakdown. Tolerate either an array or a single object, and
        // match by id then by academic year, so the itemized fees always land
        // (falling back to the list row only as a last resort).
        const list: StudentAccount[] = Array.isArray(data) ? data : data ? [data] : [];
        const detailedAccount =
          list.find((acc) => acc.id === account.id) ||
          list.find((acc) => acc.academicYearId === account.academicYearId) ||
          list[0] ||
          account;
        setSelectedAccount(detailedAccount);
      }
    } catch (error) {
      console.error('Error loading account details:', error);
    } finally {
      setLoadingAccountDetails(false);
    }
  };

  const toggleAccountSection = (section: string) => {
    setAccountDetailsExpanded(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Delete fee item
  const handleDeleteFeeItem = async (feeItem: FeeItem) => {
    // Don't allow deleting virtual fee items (tuition, registration from grid)
    if (feeItem.id.startsWith('virtual-')) {
      alert('Ce frais ne peut pas être supprimé car il provient de la grille tarifaire.');
      return;
    }
    setFeeItemToDelete(feeItem);
    setShowDeleteFeeConfirm(true);
  };

  const confirmDeleteFeeItem = async () => {
    if (!feeItemToDelete) return;

    try {
      setDeletingFeeItemId(feeItemToDelete.id);
      const response = await apiFetch(`${API_BASE}/financial/payments/fee-items/${feeItemToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        // Refresh the account details
        if (selectedAccount) {
          await handleViewAccount(selectedAccount);
        }
        await loadData();
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors de la suppression du frais');
      }
    } catch (error) {
      console.error('Error deleting fee item:', error);
      alert('Erreur lors de la suppression du frais');
    } finally {
      setDeletingFeeItemId(null);
      setShowDeleteFeeConfirm(false);
      setFeeItemToDelete(null);
    }
  };

  // Delete discount
  const handleDeleteDiscount = (discount: { id: string; discountConfig?: { name?: string } }) => {
    setDiscountToDelete(discount);
    setShowDeleteDiscountConfirm(true);
  };

  const confirmDeleteDiscount = async () => {
    if (!discountToDelete) return;

    setDeletingDiscount(true);
    try {
      const response = await apiFetch(`${API_BASE}/financial/payments/accounts/${selectedAccount?.studentId}/discounts/${discountToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        // Refresh the account details
        if (selectedAccount) {
          await handleViewAccount(selectedAccount);
        }
        await loadData();
        setShowDeleteDiscountConfirm(false);
        setDiscountToDelete(null);
        setSuccessMessage('Remise supprimée avec succès');
        setShowSuccessModal(true);
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors de la suppression de la remise');
      }
    } catch (error) {
      console.error('Error deleting discount:', error);
      alert('Erreur lors de la suppression de la remise');
    } finally {
      setDeletingDiscount(false);
    }
  };

  // Record payment
  const handleSelectAccountForPayment = (account: StudentAccount) => {
    setSelectedAccountForPayment(account);
    setShowPaymentModal(true);
    setPaymentAmount('');
    setPaymentMethod('CASH');
    setPaymentReference('');
    setPaymentNotes('');
  };

  const handleRecordPayment = async () => {
    if (!selectedAccountForPayment || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setPaymentError('Veuillez entrer un montant valide');
      return;
    }

    // Validate: amount cannot exceed remaining balance
    if (amount > selectedAccountForPayment.balance) {
      setPaymentError(`Le montant ne peut pas dépasser le solde restant (${formatCurrency(selectedAccountForPayment.balance)})`);
      return;
    }

    // Validate: reference is required for CHECK payments
    if (paymentMethod === 'CHECK' && !paymentReference.trim()) {
      setPaymentError('La référence est obligatoire pour les paiements par chèque');
      return;
    }

    setPaymentError(null);

    try {
      setRecordingPayment(true);

      // Get token from auth store
      const tokens = useAuthStore.getState().tokens;
      const authHeader: Record<string, string> = tokens?.accessToken ? { 'Authorization': `Bearer ${tokens.accessToken}` } : {};

      let response: Response;

      // Use FormData only when there's a file to upload, otherwise use JSON
      if (scannedCopy) {
        const formData = new FormData();
        formData.append('accountId', selectedAccountForPayment.id);
        formData.append('amount', amount.toString());
        formData.append('method', paymentMethod);
        if (paymentReference) formData.append('reference', paymentReference);
        if (paymentNotes) formData.append('notes', paymentNotes);
        formData.append('scannedCopy', scannedCopy);

        // Use dedicated endpoint for file uploads
        response = await apiFetch(`${API_BASE}/financial/payments/with-file`, {
          method: 'POST',
          headers: authHeader,
          body: formData,
        });
      } else {
        // Use JSON for requests without file upload
        response = await apiFetch(`${API_BASE}/financial/payments`, {
          method: 'POST',
          headers: {
            ...authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accountId: selectedAccountForPayment.id,
            amount: amount,
            method: paymentMethod,
            reference: paymentReference || undefined,
            notes: paymentNotes || undefined,
          }),
        });
      }

      if (response.ok) {
        const paymentData = await response.json();
        const paymentId = paymentData?.id || paymentData?.data?.id;

        setSelectedAccountForPayment(null);
        setPaymentAmount('');
        setPaymentReference('');
        setPaymentNotes('');
        setScannedCopy(null);
        setShowPaymentModal(false);
        setLastRecordedPaymentId(paymentId || null);
        setSuccessMessage('Paiement enregistré avec succès');
        setShowSuccessModal(true);
        await loadData();
      } else {
        const error = await response.json();
        setPaymentError(error.message || "Erreur lors de l'enregistrement du paiement");
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setPaymentError("Erreur lors de l'enregistrement du paiement");
    } finally {
      setRecordingPayment(false);
    }
  };

  // Handle delete/cancel payment
  const handleDeletePayment = async () => {
    if (!paymentToDelete || !deleteReason.trim()) return;

    try {
      setDeletingPayment(true);
      const response = await apiFetch(`${API_BASE}/financial/payments/${paymentToDelete.id}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: deleteReason }),
      });

      if (response.ok) {
        setShowDeletePaymentModal(false);
        setPaymentToDelete(null);
        setDeleteReason('');
        setSuccessMessage('Paiement supprimé avec succès');
        setShowSuccessModal(true);
        await loadData();
        // Refresh account details if modal is open
        if (selectedAccount) {
          handleViewAccount(selectedAccount);
        }
      } else {
        const error = await response.json();
        setPaymentError(error.message || "Erreur lors de la suppression du paiement");
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      setPaymentError("Erreur lors de la suppression du paiement");
    } finally {
      setDeletingPayment(false);
    }
  };

  // Handle suspend/unsuspend account - opens confirmation modal
  const handleToggleSuspension = (account: StudentAccount) => {
    setAccountToSuspend(account);
    setShowSuspensionModal(true);
  };

  // Confirm suspension action
  const confirmSuspension = async () => {
    if (!accountToSuspend) return;

    const isCurrentlyBlocked = accountToSuspend.isBlocked;
    const action = isCurrentlyBlocked ? 'unsuspend' : 'suspend';

    setSuspendingAccount(true);
    try {
      const response = await apiFetch(`${API_BASE}/financial/payments/accounts/${accountToSuspend.id}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setShowSuspensionModal(false);
        setAccountToSuspend(null);
        // Refresh accounts list
        loadData();
      } else {
        const error = await response.json();
        alert(error.message || `Erreur lors de ${isCurrentlyBlocked ? 'la levée de' : 'la'} suspension`);
      }
    } catch (error) {
      console.error('Error toggling suspension:', error);
      alert(`Erreur lors de ${isCurrentlyBlocked ? 'la levée de' : 'la'} suspension`);
    } finally {
      setSuspendingAccount(false);
    }
  };

  // Get unique academic years from accounts for filter dropdown
  const academicYears = React.useMemo(() => {
    const yearsMap = new Map<string, string>();
    accounts.forEach(account => {
      if (account.academicYear?.id && account.academicYear?.name) {
        yearsMap.set(account.academicYear.id, account.academicYear.name);
      }
    });
    return Array.from(yearsMap.entries()).map(([id, name]) => ({ id, name }));
  }, [accounts]);

  const filteredAccounts = accounts
    .filter((account) => {
      const studentName = `${account.student?.firstName || ''} ${account.student?.lastName || ''}`.toLowerCase();
      const studentCode = account.student?.studentCode?.toLowerCase() || '';
      const matchesSearch =
        studentName.includes(searchTerm.toLowerCase()) ||
        studentCode.includes(searchTerm.toLowerCase());

      const status = getAccountStatus(account);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const matchesYear = yearFilter === 'all' || account.academicYear?.id === yearFilter;
      return matchesSearch && matchesStatus && matchesYear;
    })
    // Sort alphabetically by last name, then first name
    .sort((a, b) => {
      const lastNameA = (a.student?.lastName || '').toLowerCase();
      const lastNameB = (b.student?.lastName || '').toLowerCase();
      if (lastNameA !== lastNameB) {
        return lastNameA.localeCompare(lastNameB, 'fr');
      }
      const firstNameA = (a.student?.firstName || '').toLowerCase();
      const firstNameB = (b.student?.firstName || '').toLowerCase();
      return firstNameA.localeCompare(firstNameB, 'fr');
    });

  // Summary stats
  const totalStudents = accountsTotal || accounts.length;
  const suspendedStudents = accounts.filter(a => a.isBlocked).length;
  const pendingTransfersCount = pendingTransfers.length;
  const pendingTransfersAmount = pendingTransfers.reduce((sum, t) => sum + t.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Paiements</h1>
          <p className="text-gray-500 mt-1">
            Gérez les comptes étudiants, les paiements et les virements bancaires
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Diagnostics: students a price can't be resolved for, with the reason */}
      {showUnpriced && unpriced.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-semibold text-amber-900">
                {unpriced.length} étudiant(s) sans tarif
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                Ces comptes restent à 0 MAD tant que la cause ci-dessous n'est pas corrigée.
              </p>
              <div className="mt-3 max-h-40 overflow-y-auto divide-y divide-amber-100">
                {unpriced.slice(0, 50).map((u) => (
                  <div key={u.studentCode} className="py-1.5 text-sm flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-amber-900">{u.name}</span>
                    <span className="text-amber-500">({u.studentCode})</span>
                    <span className="text-amber-500">
                      {u.filiere ?? '—'}{u.niveau ? ` · niveau ${u.niveau}` : ''}
                    </span>
                    <span className="text-amber-700">→ {u.reason}</span>
                  </div>
                ))}
                {unpriced.length > 50 && (
                  <p className="py-1.5 text-xs text-amber-600">… et {unpriced.length - 50} autre(s)</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowUnpriced(false)}
              className="text-amber-600 hover:text-amber-800 text-sm shrink-0"
            >
              Masquer
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className={`bg-white rounded-xl border p-4 cursor-pointer transition-colors ${
            activeTab === 'accounts' && statusFilter === 'all' ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
          }`}
          onClick={() => {
            setActiveTab('accounts');
            setStatusFilter('all'); // Clear all filters
          }}
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Étudiants</p>
              <p className="text-xl font-bold text-gray-900">{totalStudents}</p>
            </div>
          </div>
        </div>
        {canViewTransfers && (
          <div
            className="bg-white rounded-xl border border-gray-200 p-4 relative cursor-pointer hover:border-blue-300 transition-colors"
            onClick={() => setActiveTab('transfers')}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ArrowLeftRight className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Virements en attente</p>
                <p className="text-xl font-bold text-blue-600">{pendingTransfersCount}</p>
              </div>
            </div>
            {pendingTransfersCount > 0 && (
              <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </div>
        )}
        {canApproveDiscounts && (
          <div
            className="bg-white rounded-xl border border-gray-200 p-4 relative cursor-pointer hover:border-amber-300 transition-colors"
            onClick={() => setActiveTab('discounts')}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Remises en attente</p>
                <p className="text-xl font-bold text-amber-600">{pendingDiscountsCount}</p>
              </div>
            </div>
            {pendingDiscountsCount > 0 && (
              <span className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
            )}
          </div>
        )}
        {canViewSuspensions && (
          <div
            className={`bg-white rounded-xl border p-4 cursor-pointer transition-colors ${
              statusFilter === 'suspended' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300'
            }`}
            onClick={() => {
              setActiveTab('accounts');
              // Toggle: if already filtering suspended, clear filter
              setStatusFilter(statusFilter === 'suspended' ? 'all' : 'suspended');
            }}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <UserX className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Comptes Suspendus</p>
                <p className="text-xl font-bold text-red-600">{suspendedStudents}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('accounts')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'accounts'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Comptes Étudiants
                <span className="px-2 py-0.5 text-xs bg-gray-100 rounded-full">{totalStudents}</span>
              </span>
            </button>
            {canViewTransfers && (
              <button
                onClick={() => setActiveTab('transfers')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'transfers'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" />
                  Virements en Attente
                  {pendingTransfersCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      {pendingTransfersCount}
                    </span>
                  )}
                </span>
              </button>
            )}
            {canApproveDiscounts && (
              <button
                onClick={() => setActiveTab('discounts')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'discounts'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Remises en Attente
                  {pendingDiscountsCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                      {pendingDiscountsCount}
                    </span>
                  )}
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="space-y-4">
              {/* Error Display */}
              {accountsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="text-red-700 font-medium">Erreur de chargement</p>
                    <p className="text-red-600 text-sm">{accountsError}</p>
                  </div>
                  <button
                    onClick={() => loadData()}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                  >
                    Réessayer
                  </button>
                </div>
              )}
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Rechercher par nom ou code étudiant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Toutes les années</option>
                  {academicYears.map((year) => (
                    <option key={year.id} value={year.id}>{year.name}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="paid">Soldé</option>
                  <option value="partial">Partiel</option>
                  <option value="unpaid">Non payé</option>
                  {canViewSuspensions && <option value="suspended">Suspendu</option>}
                </select>
              </div>

              {/* Accounts Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Étudiant
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Total Dû
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Payé
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Solde
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Dû Actuel
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Prochaine Échéance
                      </th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAccounts.map((account) => {
                      const status = getAccountStatus(account);
                      const StatusIcon = statusConfig[status].icon;
                      const cumulativeDue = getCumulativeAmountDue(account);
                      const nextPayment = getNextPaymentDue(account);
                      const hasSchedule = account.schedule && account.schedule.installments && account.schedule.installments.length > 0;
                      return (
                        <tr key={account.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {account.student?.firstName} {account.student?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{account.student?.studentCode}</p>
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                            {formatCurrency(account.totalDue - (account.totalDiscount || 0))}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-right text-sm text-green-600">
                            {formatCurrency(account.totalPaid)}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-right text-sm font-semibold">
                            <span className={account.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                              {formatCurrency(account.balance)}
                            </span>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-right text-sm">
                            {cumulativeDue !== null ? (
                              <span className={cumulativeDue > 0 ? 'font-semibold text-orange-600' : 'text-green-600'}>
                                {formatCurrency(cumulativeDue)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            {nextPayment && 'isOverdue' in nextPayment && nextPayment.isOverdue ? (
                              <span className="text-red-600 text-xs font-medium">En retard</span>
                            ) : nextPayment && 'amount' in nextPayment ? (
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{formatCurrency(nextPayment.amount)}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(nextPayment.date)}
                                </p>
                              </div>
                            ) : hasSchedule ? (
                              <span className="text-green-600 text-xs font-medium">Tout payé</span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-center">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${statusConfig[status].color}`}
                            >
                              <StatusIcon size={12} />
                              {statusConfig[status].label}
                            </span>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-right">
                            <button
                              data-dropdown-trigger
                              ref={(el) => {
                                if (el) dropdownButtonRefs.current.set(account.id, el);
                              }}
                              onClick={(e) => handleOpenDropdown(account.id, e.currentTarget)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Actions"
                            >
                              <MoreVertical size={18} />
                            </button>

                            {/* Dropdown Menu */}
                            {openActionsDropdown === account.id && createPortal(
                              <div
                                ref={actionsDropdownRef}
                                className="fixed w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                                style={{
                                  top: dropdownPosition.showAbove ? 'auto' : dropdownPosition.top,
                                  bottom: dropdownPosition.showAbove ? window.innerHeight - dropdownPosition.top + 4 : 'auto',
                                  left: Math.max(8, dropdownPosition.left),
                                }}
                              >
                                {/* Voir les details */}
                                <button
                                  onClick={() => {
                                    handleViewAccount(account);
                                    setOpenActionsDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <Eye size={16} className="text-gray-400" />
                                  Voir les details
                                </button>

                                {/* Enregistrer paiement */}
                                {canRecordPayment && account.balance > 0 && (
                                  <button
                                    onClick={() => {
                                      handleSelectAccountForPayment(account);
                                      setOpenActionsDropdown(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Banknote size={16} className="text-green-500" />
                                    Enregistrer paiement
                                  </button>
                                )}

                                {/* Affecter un frais annexe */}
                                {canManageAncillary && (
                                  <button
                                    onClick={() => {
                                      setSelectedAccountForFee(account);
                                      setShowAssignFeeModal(true);
                                      setOpenActionsDropdown(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <FilePlus size={16} className="text-purple-500" />
                                    Affecter un frais annexe
                                  </button>
                                )}

                                {/* Affecter une remise */}
                                {canManageDiscounts && (
                                  <button
                                    onClick={() => {
                                      setSelectedAccountForDiscount(account);
                                      setShowAssignDiscountModal(true);
                                      setOpenActionsDropdown(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <TrendingDown size={16} className="text-amber-500" />
                                    Affecter une remise
                                  </button>
                                )}

                                {/* Assigner un echeancier */}
                                {canManageSchedules && (
                                  <button
                                    onClick={() => {
                                      setSelectedAccountForSchedule(account);
                                      setShowAssignScheduleModal(true);
                                      setOpenActionsDropdown(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Calendar size={16} className={hasSchedule ? 'text-blue-500' : 'text-gray-400'} />
                                    {hasSchedule ? `Echeancier: ${account.schedule?.template?.name || 'Assigne'}` : 'Assigner un echeancier'}
                                  </button>
                                )}

                                {/* Suspendre / Lever suspension */}
                                {canManageSuspensions && (
                                  <button
                                    onClick={() => {
                                      handleToggleSuspension(account);
                                      setOpenActionsDropdown(null);
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                      account.isBlocked
                                        ? 'text-green-700 hover:bg-green-50'
                                        : 'text-red-700 hover:bg-red-50'
                                    }`}
                                  >
                                    {account.isBlocked ? (
                                      <>
                                        <UserCheck size={16} className="text-green-500" />
                                        Lever la suspension
                                      </>
                                    ) : (
                                      <>
                                        <Ban size={16} className="text-red-500" />
                                        Suspendre le compte
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>,
                              document.body
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredAccounts.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun compte trouvé</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Ajustez vos filtres ou critères de recherche.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Transfers Tab */}
          {canViewTransfers && activeTab === 'transfers' && (
            <div className="space-y-4">
              {pendingTransfers.length > 0 ? (
                <>
                  {/* Transfer Amount Summary */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Clock className="w-6 h-6 text-yellow-600" />
                      <div>
                        <p className="text-sm text-yellow-700">
                          <strong>{pendingTransfersCount}</strong> virement(s) en attente de validation
                        </p>
                        <p className="text-lg font-semibold text-yellow-800">
                          Montant total: {formatCurrency(pendingTransfersAmount)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Transfers Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Étudiant
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Campus
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date virement
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Montant
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            Reçu
                          </th>
                          {canApproveTransfers && (
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {pendingTransfers.map((transfer) => (
                          <tr key={transfer.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {transfer.student?.firstName} {transfer.student?.lastName}
                                </p>
                                <p className="text-sm text-gray-500">{transfer.student?.studentCode}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900">
                              {transfer.campus?.name || '-'}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900">
                              {formatDate(transfer.transferDate)}
                            </td>
                            <td className="px-4 py-4 text-right font-semibold text-gray-900">
                              {formatCurrency(transfer.amount)}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {transfer.receiptUrl && (
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => {
                                      setScannedCopyError(false);
                                      setScannedCopyUrl(transfer.receiptUrl);
                                      setShowScannedCopyModal(true);
                                    }}
                                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Voir le reçu"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <a
                                    href={`${API_ORIGIN}${transfer.receiptUrl}?download=true`}
                                    download
                                    className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Télécharger le reçu"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                </div>
                              )}
                            </td>
                            {canApproveTransfers && (
                              <td className="px-4 py-4">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => openReviewModal(transfer, 'approve')}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Approuver
                                  </button>
                                  <button
                                    onClick={() => openReviewModal(transfer, 'reject')}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Rejeter
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun virement en attente</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Tous les virements ont été traités.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Pending Discounts Tab */}
          {canApproveDiscounts && activeTab === 'discounts' && (
            <div className="space-y-4">
              {loadingPendingDiscounts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : pendingDiscounts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune remise en attente</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Toutes les remises ont été traitées.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>{pendingDiscountsCount}</strong> remise(s) en attente d'approbation
                    </p>
                    <button
                      onClick={() => refetchPendingDiscounts()}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <RefreshCw size={14} />
                      Actualiser
                    </button>
                  </div>

                  {pendingDiscounts.map((discount: PendingDiscount) => (
                    <div
                      key={discount.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-amber-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <TrendingDown className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {discount.account.student.firstName} {discount.account.student.lastName}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {discount.account.student.studentCode}
                                {discount.account.student.filiereInscrit && ` • ${discount.account.student.filiereInscrit}`}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Remise</p>
                              <p className="font-medium text-gray-900">{discount.discountConfig.name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Montant</p>
                              <p className="font-semibold text-amber-600">-{formatCurrency(discount.discountAmount)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Année</p>
                              <p className="font-medium text-gray-900">{discount.academicYear?.name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Date de demande</p>
                              <p className="font-medium text-gray-900">{formatDate(discount.createdAt)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Affectée par</p>
                              <p className="font-medium text-gray-900">
                                {discount.createdByUser
                                  ? `${discount.createdByUser.firstName} ${discount.createdByUser.lastName}`
                                  : 'Système'}
                              </p>
                            </div>
                          </div>
                          {discount.notes && (
                            <p className="text-xs text-gray-500 mt-2">Note: {discount.notes}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          {rejectingDiscountId === discount.id ? (
                            <div className="space-y-2">
                              <textarea
                                value={rejectReasonInput}
                                onChange={(e) => setRejectReasonInput(e.target.value)}
                                placeholder="Raison du rejet..."
                                className="w-48 px-2 py-1 text-sm border border-gray-300 rounded"
                                rows={2}
                              />
                              <div className="flex gap-1">
                                <button
                                  onClick={() => {
                                    rejectMutation.mutate(
                                      { discountId: discount.id, reason: rejectReasonInput },
                                      {
                                        onSuccess: () => {
                                          setRejectingDiscountId(null);
                                          setRejectReasonInput('');
                                        },
                                      }
                                    );
                                  }}
                                  disabled={rejectMutation.isPending}
                                  className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  Confirmer
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectingDiscountId(null);
                                    setRejectReasonInput('');
                                  }}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          ) : canApproveDiscounts ? (
                            <>
                              <button
                                onClick={() => approveMutation.mutate(discount.id)}
                                disabled={approveMutation.isPending}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                              >
                                {approveMutation.isPending ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={14} />
                                )}
                                Approuver
                              </button>
                              <button
                                onClick={() => setRejectingDiscountId(discount.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                              >
                                <XCircle size={14} />
                                Rejeter
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-500 italic">
                              En attente d'approbation
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {reviewAction === 'approve' ? 'Approuver le virement' : 'Rejeter le virement'}
              </h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Transfer Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Étudiant</p>
                    <p className="text-sm font-medium">
                      {selectedTransfer.student?.firstName} {selectedTransfer.student?.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Montant</p>
                    <p className="text-sm font-semibold">{formatCurrency(selectedTransfer.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Date du virement</p>
                    <p className="text-sm">{formatDate(selectedTransfer.transferDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Banque</p>
                    <p className="text-sm">{selectedTransfer.bankName || '-'}</p>
                  </div>
                </div>
                {selectedTransfer.notes && (
                  <div>
                    <p className="text-xs text-gray-500">Notes de l'étudiant</p>
                    <p className="text-sm">{selectedTransfer.notes}</p>
                  </div>
                )}
                {selectedTransfer.account && (
                  <div className="pt-2 border-t border-gray-200 mt-2">
                    <p className="text-xs text-gray-500 mb-1">État du compte</p>
                    <div className="flex gap-4">
                      <span className="text-sm">
                        Solde actuel: <strong className="text-red-600">{formatCurrency(selectedTransfer.account.balance)}</strong>
                      </span>
                      <span className="text-sm">
                        Après approbation: <strong className="text-green-600">
                          {formatCurrency(selectedTransfer.account.balance - selectedTransfer.amount)}
                        </strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Fields */}
              {reviewAction === 'approve' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optionnel)
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    rows={3}
                    placeholder="Ajouter des notes sur cette approbation..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison du rejet <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    rows={3}
                    placeholder="Expliquez pourquoi ce virement est rejeté..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    L'étudiant recevra cette raison
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={submitting || (reviewAction === 'reject' && !rejectionReason.trim())}
                  className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 ${
                    reviewAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      {reviewAction === 'approve' ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Approuver
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          Rejeter
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Account Details Modal */}
      {showAccountModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-primary-50 to-blue-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedAccount.student?.firstName} {selectedAccount.student?.lastName}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedAccount.student?.studentCode} • {selectedAccount.academicYear?.name || 'N/A'}
                  </p>
                  {selectedAccount.student?.filiereInscrit && (
                    <p className="text-xs text-gray-500">{selectedAccount.student.filiereInscrit}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowAccountModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingAccountDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <p className="text-xs text-blue-600 font-medium">Total Dû</p>
                      <p className="text-lg font-bold text-blue-900">{formatCurrency(selectedAccount.totalDue)}</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <p className="text-xs text-purple-600 font-medium">Réductions</p>
                      <p className="text-lg font-bold text-purple-900">{formatCurrency(selectedAccount.totalDiscount)}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <p className="text-xs text-green-600 font-medium">Payé</p>
                      <p className="text-lg font-bold text-green-900">{formatCurrency(selectedAccount.totalPaid)}</p>
                    </div>
                    <div className={`${selectedAccount.balance > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4 text-center`}>
                      <p className={`text-xs font-medium ${selectedAccount.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        Solde
                      </p>
                      <p className={`text-lg font-bold ${selectedAccount.balance > 0 ? 'text-amber-900' : 'text-green-900'}`}>
                        {formatCurrency(selectedAccount.balance)}
                      </p>
                    </div>
                  </div>

                  {/* Fees Breakdown Section */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleAccountSection('fees')}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 font-semibold text-gray-900">
                        <FileText className="w-4 h-4" />
                        Détail des frais
                      </span>
                      {accountDetailsExpanded.includes('fees') ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {accountDetailsExpanded.includes('fees') && (
                      <div className="p-4 space-y-2">
                        {selectedAccount.feeItems && selectedAccount.feeItems.length > 0 ? (
                          <>
                            {selectedAccount.feeItems.map((fee) => {
                              const isVirtual = fee.id.startsWith('virtual-');
                              const isAncillary = fee.id.startsWith('ancillary-') || fee.ancillaryFeeAssignmentId;
                              const canDelete = !isVirtual && (isAncillary || fee.type === 'OTHER');
                              return (
                                <div key={fee.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 group">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-700">{fee.label}</span>
                                    {isAncillary && (
                                      <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded">Annexe</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{formatCurrency(fee.amount)}</span>
                                    {canDelete && (
                                      <button
                                        onClick={() => handleDeleteFeeItem(fee)}
                                        disabled={deletingFeeItemId === fee.id}
                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                        title="Supprimer ce frais"
                                      >
                                        {deletingFeeItemId === fee.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Trash2 className="w-4 h-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Subtotal and Discounts */}
                            {(() => {
                              const grossTotal = selectedAccount.feeItems.reduce((sum, f) => sum + f.amount, 0);
                              const totalDiscounts = selectedAccount.discounts?.reduce((sum, d) => sum + d.discountAmount, 0) || 0;
                              const netTotal = grossTotal - totalDiscounts;
                              const hasDiscounts = selectedAccount.discounts && selectedAccount.discounts.length > 0;

                              return (
                                <>
                                  {/* Show subtotal only if there are discounts */}
                                  {hasDiscounts && (
                                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                                      <span className="text-gray-600">Sous-total</span>
                                      <span className="font-medium text-gray-700">
                                        {formatCurrency(grossTotal)}
                                      </span>
                                    </div>
                                  )}

                                  {/* Discounts */}
                                  {hasDiscounts && (
                                    <div className="pt-2 mt-2">
                                      <p className="text-sm font-medium text-purple-600 mb-2">Réductions appliquées</p>
                                      {selectedAccount.discounts.map((discount) => (
                                        <div key={discount.id} className="flex justify-between items-center py-1 group">
                                          <span className="text-gray-600 text-sm">{discount.discountConfig?.name || 'Réduction'}</span>
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-purple-600">-{formatCurrency(discount.discountAmount)}</span>
                                            {canDeleteDiscounts && (
                                              <button
                                                onClick={() => handleDeleteDiscount(discount)}
                                                className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                                                title="Supprimer cette remise"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Total Net (or Total Frais if no discounts) */}
                                  <div className="flex justify-between items-center pt-2 border-t border-gray-300 mt-2">
                                    <span className="font-semibold text-gray-900">
                                      {hasDiscounts ? 'Total Net' : 'Total Frais'}
                                    </span>
                                    <span className="font-bold text-blue-600">
                                      {formatCurrency(netTotal)}
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            <p className="text-sm">Aucun détail de frais disponible</p>
                            <p className="text-xs text-gray-400 mt-1">Total: {formatCurrency(selectedAccount.totalDue)}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment Schedule Section */}
                  {selectedAccount.schedule && selectedAccount.schedule.installments.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleAccountSection('schedule')}
                        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <span className="flex items-center gap-2 font-semibold text-gray-900">
                          <Calendar className="w-4 h-4" />
                          Échéancier de paiement
                          {selectedAccount.schedule.template?.name && (
                            <span className="text-xs font-normal text-gray-500">
                              ({selectedAccount.schedule.template.name})
                            </span>
                          )}
                        </span>
                        {accountDetailsExpanded.includes('schedule') ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      {accountDetailsExpanded.includes('schedule') && (
                        <div className="p-4">
                          <div className="space-y-2">
                            {selectedAccount.schedule.installments.map((installment, index) => {
                              // Check if first installment includes registration fee
                              const isFirstInstallment = index === 0;
                              const otherInstallments = selectedAccount.schedule!.installments.slice(1);
                              const avgOtherAmount = otherInstallments.length > 0
                                ? otherInstallments.reduce((sum, i) => sum + i.amountDue, 0) / otherInstallments.length
                                : 0;
                              const hasRegistrationFee = isFirstInstallment && installment.amountDue > avgOtherAmount * 1.1;

                              return (
                                <div
                                  key={installment.id}
                                  className={`flex items-center justify-between p-3 rounded-lg border ${
                                    installment.status === 'PAID'
                                      ? 'bg-green-50 border-green-200'
                                      : installment.status === 'OVERDUE'
                                      ? 'bg-red-50 border-red-200'
                                      : installment.status === 'PARTIAL'
                                      ? 'bg-yellow-50 border-yellow-200'
                                      : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                                      installment.status === 'PAID'
                                        ? 'bg-green-500 text-white'
                                        : installment.status === 'OVERDUE'
                                        ? 'bg-red-500 text-white'
                                        : installment.status === 'PARTIAL'
                                        ? 'bg-yellow-500 text-white'
                                        : 'bg-gray-300 text-gray-700'
                                    }`}>
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900 text-sm">{installment.label}</p>
                                      <p className="text-xs text-gray-500">Échéance: {formatDate(installment.dueDate)}</p>
                                    </div>
                                  </div>
                                  <div className="text-right flex items-center gap-3">
                                    <div>
                                      <p className="font-semibold text-gray-900 text-sm">{formatCurrency(installment.amountDue)}</p>
                                      {hasRegistrationFee && (
                                        <p className="text-xs text-blue-600">Inclut frais d'inscription</p>
                                      )}
                                      {installment.amountPaid > 0 && installment.amountPaid < installment.amountDue && (
                                        <p className="text-xs text-green-600">Payé: {formatCurrency(installment.amountPaid)}</p>
                                      )}
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getInstallmentStatusColor(installment.status)}`}>
                                      {getInstallmentStatusLabel(installment.status)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment History Section */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleAccountSection('payments')}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 font-semibold text-gray-900">
                        <Receipt className="w-4 h-4" />
                        Historique des paiements
                        {selectedAccount.payments && (
                          <span className="text-xs font-normal text-gray-500">
                            ({selectedAccount.payments.length} paiement{selectedAccount.payments.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                      {accountDetailsExpanded.includes('payments') ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {accountDetailsExpanded.includes('payments') && (
                      <div className="p-4">
                        {selectedAccount.payments && selectedAccount.payments.length > 0 ? (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left border-b border-gray-200">
                                <th className="pb-2 text-xs font-medium text-gray-500">Date</th>
                                <th className="pb-2 text-xs font-medium text-gray-500">Montant</th>
                                <th className="pb-2 text-xs font-medium text-gray-500">Méthode</th>
                                <th className="pb-2 text-xs font-medium text-gray-500">Référence</th>
                                <th className="pb-2 text-xs font-medium text-gray-500">Statut</th>
                                <th className="pb-2 text-xs font-medium text-gray-500 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedAccount.payments.map((payment) => (
                                <tr key={payment.id}>
                                  <td className="py-2 text-gray-700">{formatDate(payment.paymentDate)}</td>
                                  <td className="py-2 font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                                  <td className="py-2 text-gray-700">{formatPaymentMethod(payment.method)}</td>
                                  <td className="py-2 text-gray-500">{payment.reference || payment.receiptNumber || '-'}</td>
                                  <td className="py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      payment.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                      payment.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {payment.status === 'PAID' ? 'Validé' : payment.status === 'CANCELLED' ? 'Annulé' : payment.status}
                                    </span>
                                    {payment.status === 'CANCELLED' && payment.cancellationReason && (
                                      <p className="text-xs text-red-600 mt-1">{payment.cancellationReason}</p>
                                    )}
                                  </td>
                                  <td className="py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      {/* Download Receipt PDF Button - Only show if user has permission */}
                                      {canGenerateReceipt && payment.status === 'PAID' && (
                                        <button
                                          onClick={async () => {
                                            try {
                                              // Fetch PDF with auth headers
                                              const response = await apiFetch(`${API_BASE}/financial/pdf/receipt/${payment.id}`, {
                                                headers: getAuthHeaders(),
                                              });
                                              if (response.ok) {
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = `recu-${payment.receiptNumber || payment.id}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                                document.body.removeChild(a);
                                              } else {
                                                alert('Erreur lors du chargement du reçu');
                                              }
                                            } catch (error) {
                                              console.error('Error loading receipt:', error);
                                              alert('Erreur lors du chargement du reçu');
                                            }
                                          }}
                                          className="p-1.5 hover:bg-green-100 rounded text-green-600 transition-colors"
                                          title="Télécharger le reçu PDF"
                                        >
                                          <FileDown className="w-4 h-4" />
                                        </button>
                                      )}
                                      {payment.scannedCopyUrl && (
                                        <button
                                          onClick={() => {
                                            setScannedCopyError(false);
                                            setScannedCopyUrl(payment.scannedCopyUrl!);
                                            setShowScannedCopyModal(true);
                                          }}
                                          className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                                          title="Voir la copie scannée"
                                        >
                                          <Image className="w-4 h-4" />
                                        </button>
                                      )}
                                      {canCancelPayment && payment.status !== 'CANCELLED' && (
                                        <button
                                          onClick={() => {
                                            setPaymentToDelete(payment);
                                            setShowDeletePaymentModal(true);
                                          }}
                                          className="p-1.5 hover:bg-red-100 rounded text-red-600 transition-colors"
                                          title="Supprimer le paiement"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            <p className="text-sm">Aucun paiement enregistré</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
              <button
                onClick={() => setShowAccountModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Fermer
              </button>
              <div className="flex items-center gap-2">
                {canManageDiscounts && (
                  <button
                    onClick={() => {
                      setSelectedAccountForDiscount(selectedAccount);
                      setShowAssignDiscountModal(true);
                    }}
                    className="px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 flex items-center gap-2"
                  >
                    <TrendingDown className="w-4 h-4" />
                    Ajouter une remise
                  </button>
                )}
                {canRecordPayment && selectedAccount.balance > 0 && (
                  <button
                    onClick={() => {
                      setShowAccountModal(false);
                      handleSelectAccountForPayment(selectedAccount);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Enregistrer un paiement
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Assign Ancillary Fee Modal */}
      {showAssignFeeModal && selectedAccountForFee && (
        <AssignAncillaryFeeModal
          studentId={selectedAccountForFee.studentId}
          studentName={[selectedAccountForFee.student?.firstName, selectedAccountForFee.student?.lastName].filter(Boolean).join(" ")}
          onClose={() => {
            setShowAssignFeeModal(false);
            setSelectedAccountForFee(null);
          }}
          onSuccess={() => {
            setShowAssignFeeModal(false);
            setSelectedAccountForFee(null);
            loadData();
          }}
        />
      )}

      {/* Assign Discount Modal */}
      {showAssignDiscountModal && selectedAccountForDiscount && (
        <AssignDiscountModal
          studentId={selectedAccountForDiscount.studentId}
          studentName={[selectedAccountForDiscount.student?.firstName, selectedAccountForDiscount.student?.lastName].filter(Boolean).join(" ")}
          onClose={() => {
            setShowAssignDiscountModal(false);
            setSelectedAccountForDiscount(null);
          }}
          onSuccess={() => {
            setShowAssignDiscountModal(false);
            setSelectedAccountForDiscount(null);
            loadData();
            // Also refresh the modal if it's open
            if (selectedAccount) {
              handleViewAccount(selectedAccount);
            }
          }}
        />
      )}

      {/* Assign Payment Schedule Modal */}
      {showAssignScheduleModal && selectedAccountForSchedule && (
        <AssignPaymentScheduleModal
          studentId={selectedAccountForSchedule.studentId}
          studentName={[selectedAccountForSchedule.student?.firstName, selectedAccountForSchedule.student?.lastName].filter(Boolean).join(" ")}
          hasExistingSchedule={!!(selectedAccountForSchedule.schedule && selectedAccountForSchedule.schedule.installments && selectedAccountForSchedule.schedule.installments.length > 0)}
          existingScheduleName={selectedAccountForSchedule.schedule?.template?.name}
          onClose={() => {
            setShowAssignScheduleModal(false);
            setSelectedAccountForSchedule(null);
          }}
          onSuccess={() => {
            setShowAssignScheduleModal(false);
            setSelectedAccountForSchedule(null);
            loadData();
            // Also refresh the account modal if it's open
            if (selectedAccount) {
              handleViewAccount(selectedAccount);
            }
          }}
        />
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedAccountForPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Enregistrer un paiement</h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedAccountForPayment(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Error Message */}
              {paymentError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800">{paymentError}</p>
                </div>
              )}

              {/* Selected Student Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {selectedAccountForPayment.student?.firstName} {selectedAccountForPayment.student?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{selectedAccountForPayment.student?.studentCode}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Solde</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(selectedAccountForPayment.balance)}</p>
                  </div>
                </div>
              </div>

              {/* Unpaid Summary - Focus on "Dû actuel" (current amount due) */}
              {selectedAccountForPayment.schedule?.installments && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  {(() => {
                    const now = new Date();
                    // Calculate overdue amount (installments past due date)
                    const overdueInstallments = selectedAccountForPayment.schedule.installments.filter(
                      (inst: any) => inst.status !== 'PAID' && new Date(inst.dueDate) < now
                    );
                    const overdueAmount = overdueInstallments.reduce(
                      (sum: number, inst: any) => sum + (inst.amountDue - inst.amountPaid), 0
                    );
                    const overdueCount = overdueInstallments.length;

                    // Next upcoming installment
                    const nextInstallment = selectedAccountForPayment.schedule.installments.find(
                      (inst: any) => inst.status !== 'PAID' && new Date(inst.dueDate) >= now
                    );

                    return (
                      <>
                        {overdueAmount > 0 ? (
                          <p className="font-medium text-red-700">
                            Dû actuel: {formatCurrency(overdueAmount)} ({overdueCount} échéance{overdueCount > 1 ? 's' : ''} en retard)
                          </p>
                        ) : (
                          <p className="font-medium text-green-700">
                            Aucun montant en retard
                          </p>
                        )}
                        {nextInstallment && (
                          <p className="text-amber-700 mt-1">
                            Prochaine échéance: {new Date(nextInstallment.dueDate).toLocaleDateString('fr-FR')} - {formatCurrency(nextInstallment.amountDue - nextInstallment.amountPaid)}
                          </p>
                        )}
                        <p className="text-gray-600 mt-1">
                          Solde total: {formatCurrency(selectedAccountForPayment.balance)}
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Payment Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max={selectedAccountForPayment.balance}
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => {
                      setPaymentAmount(e.target.value);
                      // Clear error when user changes value
                      if (paymentError) setPaymentError(null);
                    }}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                      paymentAmount && parseFloat(paymentAmount) > selectedAccountForPayment.balance
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">MAD</span>
                </div>
                {paymentAmount && parseFloat(paymentAmount) > selectedAccountForPayment.balance && (
                  <p className="text-xs text-red-600 mt-1">
                    Le montant dépasse le solde restant ({formatCurrency(selectedAccountForPayment.balance)})
                  </p>
                )}
                {selectedAccountForPayment.balance > 0 && (!paymentAmount || parseFloat(paymentAmount) <= selectedAccountForPayment.balance) && (
                  <button
                    type="button"
                    onClick={() => setPaymentAmount(selectedAccountForPayment.balance.toString())}
                    className="mt-1 text-xs text-primary-600 hover:underline"
                  >
                    Payer le solde complet ({formatCurrency(selectedAccountForPayment.balance)})
                  </button>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Méthode de paiement <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['CASH', 'CHECK', 'BANK_TRANSFER'] as const).map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setPaymentMethod(method)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        paymentMethod === method
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                      }`}
                    >
                      {formatPaymentMethod(method)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Référence {paymentMethod === 'CHECK' ? <span className="text-red-500">*</span> : <span className="text-gray-400">(optionnel)</span>}
                </label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    paymentMethod === 'CHECK' && !paymentReference.trim() ? 'border-amber-300' : 'border-gray-300'
                  }`}
                  placeholder={paymentMethod === 'CHECK' ? 'Numéro du chèque (obligatoire)' : paymentMethod === 'BANK_TRANSFER' ? 'Référence virement' : 'Référence'}
                />
              </div>

              {/* Scanned Copy Upload (for CHECK and BANK_TRANSFER only) */}
              {(paymentMethod === 'CHECK' || paymentMethod === 'BANK_TRANSFER') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Copie scannée <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-8 w-8 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="scanned-copy-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500"
                        >
                          <span>Choisir un fichier</span>
                          <input
                            id="scanned-copy-upload"
                            type="file"
                            className="sr-only"
                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                            onChange={(e) => setScannedCopy(e.target.files?.[0] || null)}
                          />
                        </label>
                        <p className="pl-1">ou glisser-déposer</p>
                      </div>
                      <p className="text-xs text-gray-500">JPG, PNG ou PDF (max 5 MB)</p>
                      {scannedCopy && (
                        <p className="text-sm text-green-600 font-medium mt-2">
                          ✓ {scannedCopy.name} ({(scannedCopy.size / 1024).toFixed(0)} KB)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes <span className="text-gray-400">(optionnel)</span>
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Notes additionnelles..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedAccountForPayment(null);
                  setPaymentAmount('');
                  setPaymentReference('');
                  setPaymentNotes('');
                  setScannedCopy(null);
                  setPaymentError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRecordPayment}
                disabled={recordingPayment || !paymentAmount}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {recordingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Receipt className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Fee Item Confirmation Dialog */}
      {showDeleteFeeConfirm && feeItemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Supprimer le frais</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600">
                Êtes-vous sûr de vouloir supprimer le frais <strong>"{feeItemToDelete.label}"</strong> de{' '}
                <strong>{formatCurrency(feeItemToDelete.amount)}</strong> ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cette action retirera ce frais du compte de l'étudiant et recalculera son solde.
                {feeItemToDelete.ancillaryFeeAssignmentId && (
                  <span className="block mt-1 text-amber-600">
                    Note: L'affectation du frais annexe sera également désactivée dans l'historique.
                  </span>
                )}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteFeeConfirm(false);
                  setFeeItemToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={deletingFeeItemId !== null}
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteFeeItem}
                disabled={deletingFeeItemId !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deletingFeeItemId !== null ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Discount Confirmation Modal */}
      {showDeleteDiscountConfirm && discountToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-scaleIn">
            {/* Header with gradient */}
            <div className="px-6 py-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Supprimer la remise</h2>
                  <p className="text-red-100 text-sm">Cette action est irréversible</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <TrendingDown className="w-5 h-5 text-purple-500" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 leading-relaxed">
                    Êtes-vous sûr de vouloir supprimer la remise{' '}
                    <span className="font-semibold text-purple-600">
                      "{discountToDelete.discountConfig?.name || 'cette remise'}"
                    </span>{' '}
                    ?
                  </p>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Important</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Cette action retirera la remise du compte de l'étudiant et recalculera automatiquement son solde.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with actions */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDiscountConfirm(false);
                  setDiscountToDelete(null);
                }}
                className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-all duration-200 hover:scale-105"
                disabled={deletingDiscount}
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteDiscount}
                disabled={deletingDiscount}
                className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl hover:from-red-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 transition-all duration-200 hover:scale-105 shadow-lg"
              >
                {deletingDiscount ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Oui, supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Payment Confirmation Modal */}
      {showDeletePaymentModal && paymentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Supprimer le paiement</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-gray-600">
                Êtes-vous sûr de vouloir supprimer ce paiement de{' '}
                <strong className="text-green-600">{formatCurrency(paymentToDelete.amount)}</strong> ?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Le paiement restera visible pour traçabilité mais ne sera plus comptabilisé dans le solde de l'étudiant.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison de la suppression <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  rows={2}
                  placeholder="Ex: Chèque sans provision, erreur de saisie..."
                  required
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeletePaymentModal(false);
                  setPaymentToDelete(null);
                  setDeleteReason('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={deletingPayment}
              >
                Annuler
              </button>
              <button
                onClick={handleDeletePayment}
                disabled={!deleteReason.trim() || deletingPayment}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deletingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full transform animate-in zoom-in-95 duration-200"
            style={{ animation: 'modalPop 0.3s ease-out' }}
          >
            <div className="p-8 text-center">
              {/* Animated Success Icon */}
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-200">
                <CheckCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
              </div>

              {/* Success Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Succès !
              </h3>

              {/* Success Message */}
              <p className="text-gray-600 mb-6">
                {successMessage}
              </p>

              {/* Download Receipt Button - Show when payment was recorded AND user has permission */}
              {canGenerateReceipt && lastRecordedPaymentId && (
                <button
                  onClick={async () => {
                    setDownloadingReceipt(true);
                    try {
                      // Fetch PDF with auth headers
                      const response = await apiFetch(`${API_BASE}/financial/pdf/receipt/${lastRecordedPaymentId}`, {
                        headers: getAuthHeaders(),
                      });
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `recu-${lastRecordedPaymentId}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } else {
                        alert('Erreur lors du téléchargement du reçu');
                      }
                    } catch (error) {
                      console.error('Error downloading receipt:', error);
                      alert('Erreur lors du téléchargement du reçu');
                    } finally {
                      setDownloadingReceipt(false);
                    }
                  }}
                  disabled={downloadingReceipt}
                  className="w-full mb-3 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-blue-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {downloadingReceipt ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Téléchargement...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-5 h-5" />
                      Télécharger le Reçu
                    </>
                  )}
                </button>
              )}

              {/* OK Button */}
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setLastRecordedPaymentId(null);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-green-200"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanned Copy Viewer Modal */}
      {showScannedCopyModal && scannedCopyUrl && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            style={{ animation: 'modalPop 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Copie scannée du paiement</h3>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${API_ORIGIN}${scannedCopyUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 transition-colors"
                  title="Ouvrir dans un nouvel onglet"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
                <button
                  onClick={() => {
                    setShowScannedCopyModal(false);
                    setScannedCopyUrl(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100 flex items-center justify-center">
              {scannedCopyError ? (
                <div className="text-center py-10 px-6 text-gray-600">
                  <p className="font-semibold text-gray-800">Aperçu indisponible</p>
                  <p className="text-sm mt-1">
                    Le fichier n'a pas pu être affiché (introuvable ou format non pris en charge par le navigateur).
                  </p>
                  <a
                    href={`${API_ORIGIN}${scannedCopyUrl}?download=true`}
                    className="inline-block mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Télécharger le fichier
                  </a>
                </div>
              ) : scannedCopyUrl.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={`${API_ORIGIN}${scannedCopyUrl}`}
                  className="w-full h-[70vh] rounded-lg border border-gray-300"
                  title="Document PDF"
                />
              ) : (
                <img
                  src={`${API_ORIGIN}${scannedCopyUrl}`}
                  alt="Copie scannée"
                  onError={() => setScannedCopyError(true)}
                  className="max-w-full max-h-[70vh] rounded-lg shadow-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suspension Confirmation Modal */}
      {showSuspensionModal && accountToSuspend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
            style={{ animation: 'modalPop 0.2s ease-out' }}
          >
            {/* Header with icon */}
            <div className={`px-6 py-8 text-center ${accountToSuspend.isBlocked ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-red-500 to-rose-600'}`}>
              <div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                {accountToSuspend.isBlocked ? (
                  <UserCheck className="w-8 h-8 text-white" />
                ) : (
                  <Ban className="w-8 h-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white">
                {accountToSuspend.isBlocked ? 'Lever la suspension' : 'Suspendre le compte'}
              </h3>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="text-center mb-6">
                <p className="text-gray-600 mb-2">
                  {accountToSuspend.isBlocked
                    ? 'Voulez-vous lever la suspension du compte de'
                    : 'Voulez-vous suspendre le compte de'
                  }
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {accountToSuspend.student?.firstName} {accountToSuspend.student?.lastName}
                </p>
                <p className="text-sm text-gray-500">
                  {accountToSuspend.student?.studentCode}
                </p>
              </div>

              {/* Warning message */}
              {!accountToSuspend.isBlocked && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Attention</p>
                      <p>L'étudiant ne pourra plus accéder à la plateforme jusqu'à la levée de la suspension.</p>
                    </div>
                  </div>
                </div>
              )}

              {accountToSuspend.isBlocked && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">Rétablissement d'accès</p>
                      <p>L'étudiant pourra à nouveau accéder à la plateforme.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Account info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Solde dû</p>
                    <p className="font-semibold text-red-600">{formatCurrency(accountToSuspend.balance)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total payé</p>
                    <p className="font-semibold text-green-600">{formatCurrency(accountToSuspend.totalPaid)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  setShowSuspensionModal(false);
                  setAccountToSuspend(null);
                }}
                disabled={suspendingAccount}
                className="flex-1 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={confirmSuspension}
                disabled={suspendingAccount}
                className={`flex-1 px-4 py-2.5 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                  accountToSuspend.isBlocked
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {suspendingAccount ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : accountToSuspend.isBlocked ? (
                  <>
                    <UserCheck className="w-4 h-4" />
                    Lever la suspension
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4" />
                    Suspendre
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animation for the modal */}
      <style>{`
        @keyframes modalPop {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
